import React from 'react';

import { IS_PRODUCTION, type UUID, isDefined } from '@nfctron/nexus-core';

import { useHotkeys } from '@mantine/hooks';
import { closeAllModals } from '@mantine/modals';

import { useSessionStore } from '@root/features/authentication/stores/sessionStore';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import type { DebuggerModalProps } from '@root/lib/modals/components/Debugger/Debugger.modal';
import { openModal } from '@root/lib/modals/utils/openModal';

import { useTranslate } from './useTranslation';

const allowUserToUseDebug = (user?: { accountId: UUID; email: string }) => {
	if (!IS_PRODUCTION) return true;

	if (!isDefined(user)) return false;

	const accountIds = [
		// ?
		'1b104dd8-9099-4522-bf8d-fded2878060d',
		// ?
		'b740694e-91d1-45be-9067-ffd096b2dfb5',
		// ?
		'53e55ece-6dd2-47c7-85f8-4865d32aeb1f',
	];
	if (accountIds.includes(user.accountId)) return true;

	const emailDomains = ['@nfctron.com'];
	if (emailDomains.some((domain) => user.email.includes(domain))) return true;

	return false;
};

/**
 * Hotkey based hook to manage debug state
 * @param {UseDebugOptions} options
 * @returns {{isDebug: boolean}}
 */
export const useDebug = (options: UseDebugOptions = {}) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const session = useSessionStore((state) => state.account);
	const debugModeAllowed = allowUserToUseDebug(session?.account);

	/** current debug state */
	const [isDebug, setIsDebug] = React.useState(false);

	/** register the hotkey */
	useHotkeys([
		[
			options.hotkey ?? 'shift+D',
			() => {
				if (!debugModeAllowed) return;
				if (isDebug) {
					setIsDebug(false);
					if (isDefined(options.modal)) closeAllModals();
				} else {
					setIsDebug(true);
					if (isDefined(options.modal)) {
						openModal('debugger', {
							title: t('modal/DebuggerModal.title'),
							innerProps: options.modal,
							fullScreen: true,
							// size: "100%",
							// overflow: 'inside',
							classNames: {
								body: 'h-full max-h-[95%]! flex flex-col',
							},
						});
					}
				}
			},
		],
	]);

	return { isDebug };
};

/**
 * Options for useDebug hook
 * @export
 */
export type UseDebugOptions = {
	hotkey?: string;
	modal?: DebuggerModalProps;
};
